const ParallelFluidicsAddress = Object.freeze({
  company: 'Parallel Fluidics',
  phone: '+1 508-989-9896',
  street1: '12 Channel Street, Suite 802',
  street2: 'Raymond L. Flynn Marine Park',
  city: 'Boston',
  state: 'MA',
  zip: '02210',
  country: 'US',
});

const StepTypes = Object.freeze({
  MOLDING: 'Molding',
  POSTMACHINING: 'Postmachining',
  BONDING: 'Bonding',
  CONNECTORIZING: 'Connectorizing',
  INSPECTION: 'Inspection',
  GENERAL: 'General',
});

const InspectionValues = Object.freeze({
  NOT_INSPECTED: 'Not inspected',
  PASSED: 'Passed',
  REJECT_MAJOR: 'Reject: Major',
  REJECT_COSMETIC: 'Reject: Cosmetic',
  TUNING: 'Tuning',
});

const FailureTagsMap = Object.freeze({
  [StepTypes.MOLDING]: [
    'Void off channel',
    'Void on channel edge',
    'Void on channel surface',
    'Contamination off channel',
    'Contamination on channel',
    'Contamination below channel',
    'Scratch',
    'Stretching',
    'Thickness',
    'Sticking - Piston side',
    'Sticking - Tool side',
    'Alignment',
    'Cracked',
    'Part Lost',
    'Other',
  ],
  [StepTypes.POSTMACHINING]: [
    'Alignment',
    'Feature depth',
    'Burrs',
    'Part kicked',
    'Part Lost',
    'Other',
  ],
  [StepTypes.BONDING]: [
    'Unbonded patch away from channel',
    'Unbonded patch near channel',
    'Unbonded patch against channel edge',
    'Unbonded patch along edge of chip',
    'Cap alignment',
    'Dimples',
    'Collapsed channels',
    'Contamination off channel',
    'Contamination on channel',
    'Scratch',
    'Part Lost',
    'Other',
  ],
  [StepTypes.CONNECTORIZING]: [
    'Alignment',
    'Not fully welded',
    'Contaminant in weld joint',
    'Failed leak test - Air side',
    'Failed leak test - Fluid side',
    'Part Lost',
    'Other',
  ],
});

const ProcessNames = Object.freeze({
  FORMING: 'forming',
  BONDING: 'bonding',
  TUNING: 'tuning',
});

const ProcessStepMap = Object.freeze({
  [ProcessNames.FORMING]: StepTypes.MOLDING,
  [ProcessNames.BONDING]: StepTypes.BONDING,
});

const StepProcessMap = Object.freeze({
  [StepTypes.MOLDING]: ProcessNames.FORMING,
  [StepTypes.BONDING]: ProcessNames.BONDING,
});

const Materials = Object.freeze({
  COP: 'COP',
  PMMA: 'PMMA',
  PC: 'PC',
});

/**
 * The specific resin grades run on the molding machines (list from Harry)
 * — what a lot label should say, unlike the customer-facing material
 * families above. Pickers seed from this but allow free text, so a new
 * grade never blocks bagging parts.
 */
const MoldingMaterials = Object.freeze({
  ZEONOR_1430R: 'COP - Zeonor 1430R',
  // eslint-disable-next-line max-len
  ZEONOR_1430R_BLACK: 'COP - Zeonor 1430R with 2% CSRM084 Carbon, 2% Barium Sulfate - CS Smart Code: RJCPE-0077',
  MEDALIST_MD_53263: 'Medalist MD-53263',
  TOPAS_E140: 'Topas E140',
  LEXAN_121R: 'Lexan 121R',
  CRYOLITE_MD_H12: 'Cryolite MD H12',
  MAKROLON_2458: 'Makrolon 2458',
  CELLENE_1060: 'Cellene 1060',
});

const MaterialCapsMap = Object.freeze({
  [Materials.COP]: [
    {
      id: '175um-coc-x1',
      display: '175 µm COC-X1 cap (recommended for most applications)',
      isDefault: true,
    },
    {
      id: '175um-coc',
      display: '175 µm COC cap (recommended for high-temp applications)',
      isDefault: false,
    },
  ],
  [Materials.PMMA]: [
    {
      id: '200um-pmma',
      display: '[New] 200 µm PMMA cap (thermally bonded)',
      isDefault: true,
    },
  ],
  [Materials.PC]: [
    {
      id: '175um-pc',
      display: '175 µm PC cap (thermally bonded)',
      isDefault: true,
    },
  ],
});

const MaterialsMap = Object.freeze({
  [Materials.COP]: {
    id: 'cop',
    display: Materials.COP,
    isDefault: false,
    cappingStyle: MaterialCapsMap[Materials.COP],
  },
  [Materials.PMMA]: {
    id: 'pmma',
    display: Materials.PMMA,
    isDefault: false,
    cappingStyle: MaterialCapsMap[Materials.PMMA],
  },
  [Materials.PC]: {
    id: 'pc',
    display: Materials.PC,
    isDefault: false,
    cappingStyle: MaterialCapsMap[Materials.PC],
  },
});

const CapMaterials = Object.freeze([
  'X1',
  'PMMA',
  'PC',
  'COC',
  'NON-STANDARD',
]);

const UncappedId = 'uncapped';

/**
 * The canonical DFM configuration string for "no optional selections", returned by
 * getDfmSelectionsString and stored as-is (quote.orderedDfmSelections, part metadata,
 * order line-item metadata). Never an empty string: Stripe drops empty-string metadata
 * values, ''-vs-missing is ambiguous, and 'none' is self-documenting in Firestore.
 * Cannot collide with a real configuration: canonical strings always contain ': '.
 * Displays must translate it (or build from getFeedbackSelections) — never render raw.
 */
const DfmSelectionsNone = 'none';

const OrderTypes = Object.freeze({
  INTERNAL: 'internal',
  CUSTOMER: 'customer',
});

const LineItemTypes = Object.freeze({
  ON_DEMAND: 'on_demand',
  PRODUCT: 'product',
});

const PartTypes = Object.freeze({
  PRODUCT: 'Product',
  TOOL: 'Tool',
  PISTON: 'Piston',
  OTHER: 'Other',
});

const PartsTabs = Object.freeze({
  LIST: 'list',
  FLOW: 'flow',
});

const ShippingSteps = Object.freeze({
  PACKING_INFO: 'Package Info',
  CUSTOMS: 'Customs',
  PARCEL: 'Parcel',
  SELECT_RATE: 'Select Rate',
  CONFIRM: 'Confirm',
});

const OrderStatus = Object.freeze({
  NOT_STARTED: 'Not started',
  IN_PROGRESS: 'In progress',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
});

const OrderStatusColorMap = Object.freeze({
  [OrderStatus.NOT_STARTED]: 'grey',
  [OrderStatus.IN_PROGRESS]: 'blue',
  [OrderStatus.COMPLETED]: 'green',
  [OrderStatus.ARCHIVED]: 'grey',
});

const InventoryCommitmentTypes = Object.freeze({
  INCOMING_SUPPLY: 'INCOMING_SUPPLY',
  COMMITTED_DEMAND: 'COMMITTED_DEMAND',
});

const InventoryCommitmentStatus = Object.freeze({
  OPEN: 'OPEN',
  FULFILLED: 'FULFILLED',
  CANCELLED: 'CANCELLED',
});

const ReorderSuggestionStatus = Object.freeze({
  OPEN: 'OPEN',
  FULFILLED: 'FULFILLED',
  DISMISSED: 'DISMISSED',
  CANCELLED: 'CANCELLED',
});

const ReorderSuggestionCancellationReasons = Object.freeze({
  VERSION_SUPERSEDED: 'VERSION_SUPERSEDED',
  POLICY_DISABLED: 'POLICY_DISABLED',
  INVENTORY_RECOVERED: 'INVENTORY_RECOVERED',
});

const DefaultTolerance = 0.125;

const LeadtimeDisplayMap = Object.freeze({
  'leadtime-standard': 'Standard',
  'leadtime-expedited': 'Expedited',
  'leadtime-rush': 'Rush',
});

const LotKinds = Object.freeze({
  INHOUSE: 'In-house',
  ASSEMBLED: 'Assembled',
  PURCHASED: 'Purchased',
});

/** Molding machines — recorded on in-house lots to trace which press made the parts. */
const BabyMachines = Object.freeze({
  BABYPLAST_1: 'BabyPlast 1',
  BABYPLAST_2: 'BabyPlast 2',
});

const LotStatus = Object.freeze({
  AWAITING_INSPECTION: 'Awaiting inspection',
  SEALED: 'Sealed',
  OPEN: 'Open',
  EMPTY: 'Empty',
  QUARANTINED: 'Quarantined',
});

const LotInspectionResults = Object.freeze({
  PASSED: 'Passed',
  FAILED: 'Failed',
});

const LotEventTypes = Object.freeze({
  CREATED: 'created',
  INSPECTED: 'inspected',
  STATUS_CHANGED: 'status_changed',
  CONSUMED: 'consumed',
  ADJUSTED: 'adjusted',
  SOURCES_EDITED: 'sources_edited',
  LABEL_PRINTED: 'label_printed',
});

/** Stable ids for the label-printing stations, for referencing one by role. */
const LabelPrinterIds = Object.freeze({
  MOLDING: 'pi-printer',
  CLEAN_ROOM: 'pi-printer-2',
});

/**
 * Label-printing stations (Raspberry Pi + Brother QL). Single source of
 * truth for the admin printer dropdown (id + name) and the Cloud Functions'
 * request routing (id → host). Adding a station is one entry here.
 */
const LabelPrinters = Object.freeze([
  Object.freeze({
    id: LabelPrinterIds.MOLDING,
    name: 'Brother QL-810W',
    host: 'pi-printer.parallelfluidics.com',
  }),
  Object.freeze({
    id: LabelPrinterIds.CLEAN_ROOM,
    name: 'Brother QL-800 – Clean room',
    host: 'pi-printer-2.parallelfluidics.com',
  }),
]);

/** The station used when a caller doesn't specify one. */
const DefaultLabelPrinterId = LabelPrinterIds.MOLDING;

const PPartStatus = Object.freeze({
  ACTIVE: 'Active',
  IN_REVIEW: 'In Review',
  SUSTAINED: 'Sustained',
  OBSOLETE: 'Obsolete',
  BROKEN: 'Broken',
});

/**
 * Statuses no picker offers (part links, BOM, run tools/pistons): retired or
 * unusable parts stay in the parts list but can't be referenced anew.
 */
const PPartRetiredStatuses = Object.freeze([PPartStatus.OBSOLETE, PPartStatus.BROKEN]);

const PPartStatusMap = Object.freeze({
  [PPartStatus.ACTIVE]: {
    description: 'Currently being made and actively used.',
    color: 'green',
  },
  [PPartStatus.IN_REVIEW]: {
    description: 'Under design or review and not yet released for use.',
    color: 'severity-medium',
  },
  [PPartStatus.SUSTAINED]: {
    description: 'No longer being made or produced, but still actively used.',
    color: 'blue',
  },
  [PPartStatus.OBSOLETE]: {
    description: 'An older version that is no longer used or recommended.',
    color: 'severity-high',
  },
  [PPartStatus.BROKEN]: {
    description: 'Broken, unsupported, or completely unusable. Should not be used under any circumstances.',
    color: 'red',
  },
});

export {
  ParallelFluidicsAddress,
  StepTypes,
  InspectionValues,
  FailureTagsMap,
  ProcessNames,
  ProcessStepMap,
  StepProcessMap,
  Materials,
  MaterialsMap,
  MaterialCapsMap,
  MoldingMaterials,
  CapMaterials,
  UncappedId,
  DfmSelectionsNone,
  OrderTypes,
  LineItemTypes,
  PartTypes,
  PartsTabs,
  ShippingSteps,
  OrderStatus,
  OrderStatusColorMap,
  InventoryCommitmentTypes,
  InventoryCommitmentStatus,
  ReorderSuggestionStatus,
  ReorderSuggestionCancellationReasons,
  DefaultTolerance,
  LotInspectionResults,
  LeadtimeDisplayMap,
  LabelPrinterIds,
  LabelPrinters,
  DefaultLabelPrinterId,
  LotEventTypes,
  LotKinds,
  LotStatus,
  BabyMachines,
  PPartStatus,
  PPartRetiredStatuses,
  PPartStatusMap,
};
